// ─────────────────────────────────────────────────────────────────────────────
// useRaceMode — Race-start arming + 500m voice cues for the Monitor page.
//
// Two cooperating helpers:
//
// 1. Arming flow (`armRace`)
//    • While "armed", listens to `devicemotion` events.
//    • Computes |a − g| (gravity-subtracted accel magnitude).
//    • Fires `onLaunch()` once the magnitude exceeds RACE_LAUNCH_THRESHOLD
//      for at least RACE_LAUNCH_HOLD_MS — ignoring small fidgets.
//    • Falls back to PM5 stroke detection (any stroke during arm = launch)
//      so erg races also work.
//
// 2. 500m voice cues (`watchDistance`)
//    • Each time the rower crosses a 500m boundary, speaks a short cue
//      via the browser's SpeechSynthesis API. Last 250m is special.
//
// Both helpers no-op outside the browser.
// ─────────────────────────────────────────────────────────────────────────────

import { useCallback, useEffect, useRef, useState } from 'react';

const RACE_LAUNCH_THRESHOLD = 6.0;   // m/s² above gravity (~0.6 g) — boat lurch
const RACE_LAUNCH_HOLD_MS   = 180;   // sustained for this long → launch
const RACE_AUDIO_RATE       = 1.0;   // SpeechSynthesis rate
const RACE_AUDIO_PITCH      = 1.0;
const RACE_AUDIO_VOLUME     = 1.0;

// ── Cox voice mute flag — persisted to localStorage so user preferences survive
// page reloads and sync across the Monitor / Coxswain / Settings screens.
const COX_VOICE_KEY = 'crewz_cox_voice_enabled';
const COX_VOICE_PROFILE_KEY = 'crewz_cox_voice_profile';

// Four motivational voice profiles — all share the energetic "Pump-Up"
// character (slightly elevated pitch + faster rate) the rower picked as
// their favourite. Voice hints prioritise the platform-native voices most
// likely to match each gender + delivery; speak() falls back gracefully.
export const COX_VOICE_PROFILES = [
  { id: 'male_hype',    label: 'Male · Hype',      gender: 'male',   pitch: 1.10, rate: 1.10, voiceHints: ['Daniel (Enhanced)', 'Aaron', 'Tom', 'Google US English', 'Microsoft Mark'] },
  { id: 'male_charge',  label: 'Male · Charge',    gender: 'male',   pitch: 1.05, rate: 1.15, voiceHints: ['Alex', 'Fred', 'Daniel', 'Microsoft David', 'Google UK English Male'] },
  { id: 'female_pump',  label: 'Female · Pump-Up', gender: 'female', pitch: 1.15, rate: 1.10, voiceHints: ['Samantha', 'Victoria', 'Google UK English Female', 'Microsoft Zira'] },
  { id: 'female_fire',  label: 'Female · Fire',    gender: 'female', pitch: 1.20, rate: 1.05, voiceHints: ['Karen', 'Allison', 'Ava', 'Moira', 'Microsoft Eva', 'Google US English Female'] },
];
export const DEFAULT_COX_VOICE_PROFILE = 'female_pump';

// Map legacy profile IDs → new IDs so rowers who picked one of the old
// voices in a previous build don't end up with an "unknown" profile after
// the energetic-only refresh.
const LEGACY_PROFILE_MIGRATION = {
  male_drill:   'male_charge',
  male_coach:   'male_hype',
  female_coach: 'female_fire',
};

export function isCoxVoiceEnabled() {
  try {
    if (typeof localStorage === 'undefined') return true;
    const raw = localStorage.getItem(COX_VOICE_KEY);
    if (raw === null) return true;      // default ON
    return raw === 'true';
  } catch { return true; }
}

export function setCoxVoiceEnabled(enabled) {
  try {
    if (typeof localStorage === 'undefined') return;
    localStorage.setItem(COX_VOICE_KEY, enabled ? 'true' : 'false');
    // Broadcast so other tabs / components can react immediately.
    window.dispatchEvent(new CustomEvent('crewz-cox-voice-changed', { detail: { enabled } }));
    // Silence any in-flight utterance the moment the rower turns cox off.
    if (!enabled && typeof window !== 'undefined' && 'speechSynthesis' in window) {
      try { window.speechSynthesis.cancel(); } catch (_e) { /* noop */ }
    }
  } catch (_e) { /* swallow */ }
}

export function getCoxVoiceProfile() {
  try {
    if (typeof localStorage === 'undefined') return DEFAULT_COX_VOICE_PROFILE;
    const raw = localStorage.getItem(COX_VOICE_PROFILE_KEY);
    if (!raw) return DEFAULT_COX_VOICE_PROFILE;
    // Migrate retired IDs from older builds.
    if (LEGACY_PROFILE_MIGRATION[raw]) {
      const migrated = LEGACY_PROFILE_MIGRATION[raw];
      try { localStorage.setItem(COX_VOICE_PROFILE_KEY, migrated); } catch (_e) { /* noop */ }
      return migrated;
    }
    return raw;
  } catch { return DEFAULT_COX_VOICE_PROFILE; }
}

export function setCoxVoiceProfile(profileId) {
  try {
    if (typeof localStorage === 'undefined') return;
    localStorage.setItem(COX_VOICE_PROFILE_KEY, profileId);
    window.dispatchEvent(new CustomEvent('crewz-cox-voice-profile-changed', { detail: { profileId } }));
  } catch (_e) { /* swallow */ }
}

/** Find the best system voice for the active profile (no-op if API absent). */
function pickSystemVoice(profile) {
  try {
    if (typeof window === 'undefined' || !window.speechSynthesis) return null;
    const voices = window.speechSynthesis.getVoices() || [];
    if (voices.length === 0) return null;
    for (const hint of profile.voiceHints || []) {
      const match = voices.find(v => v.name === hint || v.name?.includes(hint));
      if (match) return match;
    }
    // Fallback by language + heuristic gender from name
    const enVoices = voices.filter(v => /^en/i.test(v.lang || ''));
    if (enVoices.length === 0) return null;
    const wantsFemale = profile.gender === 'female';
    const FEMALE_NAMES = /Samantha|Victoria|Karen|Moira|Susan|Allison|Ava|Zira|Eva|Hazel|female/i;
    const MALE_NAMES = /Daniel|Alex|Fred|Aaron|Tom|David|Mark|Oliver|male/i;
    return enVoices.find(v => (wantsFemale ? FEMALE_NAMES : MALE_NAMES).test(v.name)) || enVoices[0];
  } catch { return null; }
}

export function speak(text, { profileOverride = null, bypassEnabled = false } = {}) {
  try {
    if (!bypassEnabled && !isCoxVoiceEnabled()) return;
    if (typeof window === 'undefined' || !('speechSynthesis' in window)) return;
    const profileId = profileOverride || getCoxVoiceProfile();
    const profile = COX_VOICE_PROFILES.find(p => p.id === profileId) || COX_VOICE_PROFILES[0];
    const u = new window.SpeechSynthesisUtterance(text);
    u.rate = profile.rate ?? RACE_AUDIO_RATE;
    u.pitch = profile.pitch ?? RACE_AUDIO_PITCH;
    u.volume = RACE_AUDIO_VOLUME;
    const voice = pickSystemVoice(profile);
    if (voice) u.voice = voice;
    // Cancel any in-flight utterance so cues never queue up
    window.speechSynthesis.cancel();
    window.speechSynthesis.speak(u);
  } catch (_e) { /* swallow */ }
}

// Short sample lines coaches can preview — one per profile so each cox has
// its own personality on first tap. Falls back to a generic line.
const PREVIEW_PHRASES = {
  male_hype:    'Let\'s go — bring the heat!',
  male_charge:  'Drive it — drive it now!',
  female_pump:  'Let\'s go — this is your race!',
  female_fire:  'Send it — every stroke counts!',
};

/**
 * Speak a short sample line in the given cox profile so the rower can audition
 * voices before committing. Bypasses the global cox-voice toggle (the user is
 * explicitly tapping to hear it) and queues nothing — each tap interrupts the
 * previous sample.
 */
export function previewCoxVoiceProfile(profileId) {
  const phrase = PREVIEW_PHRASES[profileId] || 'This is your race — take it!';
  speak(phrase, { profileOverride: profileId, bypassEnabled: true });
}

// ── Race encouragement phrase bank — weighted by race position. ─────────────
// Buckets reflect the four phases of a race: build-out, mid-race grind,
// closing push, and sprint-to-the-line.
export const RACE_PHRASES = {
  early: [     // 0–30% done   (e.g. 500m of a 2K)
    'Five hundred down — keep building!',
    'Find the swing!',
    'Together — hold the rhythm!',
    'Let the boat run!',
    'Drive it home!',
  ],
  middle: [    // 30–60% done  (e.g. 1000m of a 2K)
    "Halfway — you're flying, stay with it!",
    'This is your race — take it!',
    'Dig deep — this is where it counts!',
    'You are stronger than you think!',
    'Every stroke counts — make them all matter!',
  ],
  late: [      // 60–85% done  (e.g. 1500m of a 2K)
    'Five hundred to go — empty the tank!',
    'Leave it all on the water!',
    'Last five hundred — this is what you came for!',
    'Final push — make it count!',
    "You've got this — bring it home!",
  ],
  sprint: [    // ≥85% done    (final 250m)
    'GO, GO, GO!',
    'This is your time — GO!',
    'Heart and legs — give me everything!',
    'Now we race!',
    "Don't give them an inch!",
  ],
};

function pickRandom(arr) {
  if (!Array.isArray(arr) || arr.length === 0) return null;
  return arr[Math.floor(Math.random() * arr.length)];
}

/**
 * Pick an encouragement phrase based on % of race completed.
 * @param {number} distanceRemaining  meters left
 * @param {number} totalDistance      race target meters
 * @param {object} [opts]
 * @param {boolean} [opts.forceSprint] true => always pull from sprint bucket
 *   (used by the final-250m cue regardless of % so short races still get the
 *   sprint flavour they deserve)
 */
export function getRaceEncouragement(distanceRemaining, totalDistance, opts = {}) {
  if (opts.forceSprint) return pickRandom(RACE_PHRASES.sprint);
  const pctDone = totalDistance > 0 ? 1 - (distanceRemaining / totalDistance) : 0;
  if (pctDone < 0.3)  return pickRandom(RACE_PHRASES.early);
  if (pctDone < 0.6)  return pickRandom(RACE_PHRASES.middle);
  if (pctDone < 0.85) return pickRandom(RACE_PHRASES.late);
  return pickRandom(RACE_PHRASES.sprint);
}

/** Default cue copy. Distance is meters remaining or completed. */
export function buildRaceCue({ done, total }) {
  const remaining = Math.max(0, total - done);
  // Final-250m cue: ALWAYS sprint flavour, even on short races where the %
  // bucket would still register as "late".
  if (remaining > 0 && remaining <= 250) {
    return getRaceEncouragement(remaining, total, { forceSprint: true });
  }
  if (done > 0 && done < total) {
    return getRaceEncouragement(remaining, total);
  }
  return null;
}

export function useRaceMode() {
  const [armed, setArmed] = useState(false);
  const [magnitude, setMagnitude] = useState(0);
  const [permissionNeeded, setPermissionNeeded] = useState(false);
  const onLaunchRef = useRef(null);
  const aboveSinceRef = useRef(null);

  const handleMotion = useCallback((event) => {
    const a = event.accelerationIncludingGravity || event.acceleration;
    if (!a) return;
    const ax = a.x || 0;
    const ay = a.y || 0;
    const az = a.z || 0;
    const mag = Math.sqrt(ax * ax + ay * ay + az * az);
    // Subtract gravity baseline (≈9.81). Use abs so orientation doesn't matter.
    const delta = Math.abs(mag - 9.81);
    setMagnitude(delta);
    if (delta > RACE_LAUNCH_THRESHOLD) {
      if (aboveSinceRef.current == null) aboveSinceRef.current = Date.now();
      else if (Date.now() - aboveSinceRef.current >= RACE_LAUNCH_HOLD_MS) {
        // Fire launch
        const cb = onLaunchRef.current;
        aboveSinceRef.current = null;
        setArmed(false);
        if (typeof cb === 'function') cb('motion');
      }
    } else {
      aboveSinceRef.current = null;
    }
  }, []);

  /** Arm the race — start listening for the launch event. */
  const armRace = useCallback(async (onLaunch) => {
    onLaunchRef.current = onLaunch;
    aboveSinceRef.current = null;
    // iOS 13+ requires explicit motion permission
    if (typeof window !== 'undefined'
        && typeof window.DeviceMotionEvent !== 'undefined'
        && typeof window.DeviceMotionEvent.requestPermission === 'function') {
      try {
        const result = await window.DeviceMotionEvent.requestPermission();
        if (result !== 'granted') {
          setPermissionNeeded(true);
          return false;
        }
      } catch (_e) {
        setPermissionNeeded(true);
        return false;
      }
    }
    setPermissionNeeded(false);
    setArmed(true);
    return true;
  }, []);

  const cancelArm = useCallback(() => {
    onLaunchRef.current = null;
    aboveSinceRef.current = null;
    setArmed(false);
  }, []);

  /** Manual override — useful when phone has no motion access (laptop / erg). */
  const manualLaunch = useCallback(() => {
    const cb = onLaunchRef.current;
    aboveSinceRef.current = null;
    setArmed(false);
    if (typeof cb === 'function') cb('manual');
  }, []);

  // Hook devicemotion listener only while armed
  useEffect(() => {
    if (!armed || typeof window === 'undefined') return undefined;
    window.addEventListener('devicemotion', handleMotion, { passive: true });
    return () => window.removeEventListener('devicemotion', handleMotion);
  }, [armed, handleMotion]);

  return {
    armed,
    magnitude,
    permissionNeeded,
    armRace,
    cancelArm,
    manualLaunch,
    threshold: RACE_LAUNCH_THRESHOLD,
  };
}

/**
 * Watch distance during a race and fire voice cues at every 500m boundary
 * plus a special "250 to go" cue for the closing stretch.
 *
 * @param {object} args
 *   @param {boolean} args.active  — only fires while true (i.e. raceMode workout running)
 *   @param {number}  args.distance — current meters rowed
 *   @param {number}  args.target   — race distance in meters (e.g. 2000)
 */
export function useRaceCues({ active, distance, target }) {
  const lastCueAtRef = useRef(0);          // meters of last 500m cue fired
  const finalCueFiredRef = useRef(false);  // "250 to go" fired once

  // Reset cue trackers when entering a new race
  useEffect(() => {
    if (!active) {
      lastCueAtRef.current = 0;
      finalCueFiredRef.current = false;
      try { window?.speechSynthesis?.cancel(); } catch (_e) { /* noop */ }
    }
  }, [active]);

  useEffect(() => {
    if (!active || typeof target !== 'number' || target <= 0) return;
    const d = Math.floor(distance || 0);

    // 500m boundary cue (don't fire 0m or final boundary if it equals target)
    const nextBoundary = Math.floor(d / 500) * 500;
    if (nextBoundary > 0 && nextBoundary < target && nextBoundary > lastCueAtRef.current) {
      const cue = buildRaceCue({ done: nextBoundary, total: target });
      if (cue) speak(cue);
      lastCueAtRef.current = nextBoundary;
    }

    // Last 250m cue — once
    const remaining = target - d;
    if (!finalCueFiredRef.current && remaining > 0 && remaining <= 250) {
      const cue = buildRaceCue({ done: d, total: target });
      if (cue) speak(cue);
      finalCueFiredRef.current = true;
    }
  }, [active, distance, target]);
}
